<template>
  <PromoBanner />
  <header
    :style="`--background-height: ${backgroundHeight};`"
    :class="
      $b({
        mainMenuOpen,
        isTransparent,
        isInverted,
        isBackdropActive,
        isScrolled,
        isScrollingDown,
        showBlackLinks,
        '--no-javascript': !isLoaded,
      })
    "
  >
    <nav :class="$b('wrapper')">
      <!-- Main nav items -->
      <div :class="$b('main')">
        <!-- Logo -->
        <VisionLink
          :to="resolveLocalePath('/')"
          title="SunGod Homepage"
          display="block"
          :class="$b('logo')"
          @click="handleLogoClick"
        >
          <span class="sr-only">SunGod</span>
          <BaseLogo />
        </VisionLink>
        <!-- Main links: Sunglasses -> Everyday -> Mens -->
        <ul
          v-if="!simplified"
          :class="$b('level-1-links')"
        >
          <li
            v-for="(primaryNavItem, primaryNavItemIndex) in mainNavigationData
              .mainNavigation.mainNavigationItems"
            :key="primaryNavItem.id"
            :style="`--level-1-index: ${primaryNavItemIndex}`"
            :class="$b('level-1-wrapper')"
            @mousemove="handleLevel1HoverEnter($event)"
            @mouseleave="handleLevel1HoverExit"
          >
            <component
              :is="primaryNavItem.link?.internalLink ? 'a' : 'button'"
              v-if="primaryNavItem._modelApiKey === 'main_navigation_button'"
              :class="$b('level-1-link', {'has-theme': primaryNavItem.gradient})"
              :href="buildInternalLink(primaryNavItem.link ?? null)"
              @click="
                toggleLevel2Menu(
                  primaryNavItemIndex,
                  $event,
                  primaryNavItem.buttonText
                )
              "
            >
              <span :class="$b('level-1-link-text')">
                {{ primaryNavItem.buttonText }}
              </span>
            </component>
            <a
              v-else-if="
                primaryNavItem._modelApiKey === 'main_navigation_link' &&
                  primaryNavItem.internalLink?.slug
              "
              :class="$b('level-1-link', {'has-theme': primaryNavItem.gradient})"
              :href="buildInternalLink(primaryNavItem)"
              @click="handleLevel1Event(primaryNavItem.linkText)"
            >
              <span :class="$b('level-1-link-text')">
                {{ primaryNavItem.linkText }}
              </span>
            </a>
            <div
              v-if="primaryNavItem?.nestedItems?.length"
              :class="
                $b('level-2-links', {
                  open: activeLevel2Menu === primaryNavItemIndex,
                  isImageGrid: ['image-grid', 'product-image-grid'].includes(
                    primaryNavItem.nestedItemsLayout
                  ),
                  isProductImageGrid:
                    primaryNavItem.nestedItemsLayout === 'product-image-grid',
                  isLinkGrid: primaryNavItem.nestedItemsLayout === 'link-grid',
                })
              "
            >
              <button
                :class="$b('back-button')"
                @click="toggleLevel2Menu(primaryNavItemIndex, $event)"
              >
                <VisionIcon
                  icon="chevron"
                  colour="grey-mid"
                  :rotate="180"
                  size="xx-small"
                />
                Back
              </button>
              <ul :class="$b('level-2-wrapper')">
                <li :class="$b('level-2-heading')">
                  {{ primaryNavItem.buttonText }}
                </li>
                <li
                  v-for="(
                    level2Item, level2ItemIndex
                  ) in primaryNavItem.nestedItems"
                  :key="level2Item.id"
                  :style="`--level-2-index: ${level2ItemIndex}`"
                  :class="$b('level-2-item')"
                >
                  <div
                    :class="$b('level-2-link')"
                    @click="
                      handleLevel2Event(level2Item, primaryNavItem.buttonText)
                    "
                  >
                    <MainNavButton
                      v-if="
                        level2Item._modelApiKey === 'main_navigation_button'
                      "
                      :nav-item="level2Item"
                      :index="level2ItemIndex"
                      :has-link="level2Item.link?.internalLink ? true : false"
                      :link="buildInternalLink(level2Item.link)"
                      @toggle-level-3-menu="toggleLevel3Menu"
                    />
                    <MainNavLink
                      v-else-if="
                        level2Item._modelApiKey === 'main_navigation_link'
                      "
                      :nav-item="level2Item"
                      @toggle-level-3-menu="toggleLevel3Menu"
                    />
                    <MainNavImageCard
                      v-else-if="
                        level2Item._modelApiKey ===
                          'main_navigation_image_card' &&
                          level2Item.internalLink?.slug
                      "
                      :nav-item="level2Item"
                    />
                  </div>
                  <ul
                    v-if="level2Item?.nestedItems?.length"
                    :class="
                      $b('level-3-links', {
                        open: activeLevel3Menu === level2ItemIndex,
                        isLoaded,
                      })
                    "
                  >
                    <li
                      v-for="(
                        level3Item, level3ItemIndex
                      ) in level2Item.nestedItems"
                      :key="level3Item.id"
                      :style="`--level-3-index: ${level3ItemIndex}`"
                      :class="$b('level-3-link')"
                    >
                      <a
                        v-if="
                          level3Item._modelApiKey === 'main_navigation_link' &&
                            level3Item.internalLink?.slug
                        "
                        :class="$b('level-3-link-inner')"
                        :href="buildInternalLink(level3Item)"
                        @click="
                          handleLevel3Event(
                            level3Item.linkText,
                            level2Item.buttonText,
                            primaryNavItem.buttonText
                          )
                        "
                      >
                        {{ level3Item.linkText }}
                      </a>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </li>
          <li
            :class="
              $b('utility-item', {
                country: true,
              })
            "
            :style="`--level-1-index: ${mainNavigationData.mainNavigation.mainNavigationItems.length}`"
          >
            <VisionButton
              :outline="true"
              size="x-small"
              background-colour="black"
              display="flex"
              :class="$b('country-button')"
              @click="showCurrencyMenu"
              @mouseover="countryChevronColour = 'white'"
              @mouseleave="countryChevronColour = 'black'"
            >
              <template #content>
                <span :class="$b('country-button-text')">
                  {{ $i18n.localeProperties.value.currency }}
                  <span :class="$b('country-button-chevron')">
                    <VisionIcon
                      icon="chevron"
                      size="stretch"
                      :colour="countryChevronColour"
                    />
                  </span>
                </span>
              </template>
            </VisionButton>
          </li>
        </ul>
      </div>
      <!-- Utilities -->
      <ul :class="$b('utilities')">
        <li :class="$b('utility-item')">
          <VisionLink
            :class="$b('utility-button')"
            :is-button="true"
            @click="handleCartClick"
          >
            <span :class="$b('utility-icon')">
              <VisionIcon
                icon="bag"
                colour="black"
                gradient="none"
                size="stretch"
              />
              <span :class="[$b('cart-icon-basket-count'), 'ignore-theme']">
                <span
                  v-show="!isLoaded || shopifyStore.loading"
                  :class="$b('cart-icon-basket-count-loader')"
                />
                <ClientOnly>
                  {{ shopifyStore.getOptimisticCartCount }}
                </ClientOnly>
              </span>
            </span>
          </VisionLink>
        </li>
        <li
          v-if="!simplified"
          :class="
            $b('utility-item', {
              country: true,
            })
          "
        >
          <VisionButton
            :outline="true"
            size="xx-small"
            background-colour="black"
            display="flex"
            :class="$b('country-button')"
            @click="showCurrencyMenu"
            @mouseover="countryChevronColour = 'white'"
            @mouseleave="countryChevronColour = 'black'"
          >
            <template #content>
              <span :class="$b('country-button-text')">
                {{ $i18n.localeProperties.value.currency }}
                <span :class="$b('country-button-chevron')">
                  <VisionIcon
                    icon="chevron"
                    size="stretch"
                    :colour="countryChevronColour"
                  />
                </span>
              </span>
            </template>
          </VisionButton>
        </li>
        <li
          v-if="!simplified"
          :class="
            $b('utility-item', {
              menu: true,
            })
          "
          @click="openMainMenu"
        >
          <button :class="$b('utility-button')">
            <span :class="$b('utility-icon-menu')" />
          </button>
        </li>
      </ul>
    </nav>
  </header>
</template>
<script>
import mainNavigationQuery from '~/gql/mainNavigation.gql'
import { throttleFunction } from '~/assets/js/helpers'

export default defineNuxtComponent({
  name: 'MainNav',

  props: {
    simplified: { type: Boolean, default: false },
    isTransparent: { type: Boolean, default: false },
    isInverted: { type: Boolean, default: false },
  },

  async setup() {
    const { resolveLocalePath, buildInternalLink } = useUrlBuilder()
    const sidebarStore = useSidebarStore()
    const shopifyStore = useShopifyStore()
    const { localeForDato } = useDatoLocale()
    const { isMdBreakpointOrAbove } = useBreakpoint()
    const mainMenuOpen = ref(false)
    const activeLevel2Menu = ref(null)
    const activeLevel3Menu = ref(null)
    const isLoaded = ref(false)
    const isBackdropActive = ref(false)
    const isScrollingDown = ref(false)
    const isScrolled = ref(false)
    const backgroundHeight = ref('0px')
    const countryChevronColour = ref('black')
    let previousScroll = 0

    const { data: mainNavigationData } = await useDatoQuery({
      query: mainNavigationQuery,
      prefetchedRequest: 'mainNavigation',
      variables: {
        locale: localeForDato,
      },
    })

    const throttledScrollHandler = throttleFunction(() => {
      const MainNavElem = document.querySelector('.MainNav')
      const announcementBarHeight = parseInt(
        getComputedStyle(MainNavElem).getPropertyValue('--promo-bar-height')
      )
      const navHeight = parseInt(
        getComputedStyle(MainNavElem).getPropertyValue('--nav-height')
      )
      const scrollTrigger = navHeight + announcementBarHeight

      isScrollingDown.value =
        scrollY > scrollTrigger && scrollY > previousScroll
      isScrolled.value = scrollY > scrollTrigger + 120

      previousScroll = scrollY
    }, 200)

    return {
      resolveLocalePath,
      buildInternalLink,
      sidebarStore,
      mainMenuOpen,
      activeLevel2Menu,
      activeLevel3Menu,
      isLoaded,
      isBackdropActive,
      isScrollingDown,
      isScrolled,
      backgroundHeight,
      mainNavigationData,
      countryChevronColour,
      throttledScrollHandler,
      isMdBreakpointOrAbove,
      shopifyStore
    }
  },

  computed: {
    showBlackLinks() {
      return !!this.mainNavigationData.mainNavigation.
        pagesWithBlackNavLinks?.find(
          (link) => (this.buildInternalLink(link) ===
            this.resolveLocalePath(this.$route.path)
          )
        )
    }
  },

  methods: {
    handleLogoClick() {
      this.$track.navigation.logoClicked()
    },

    openMainMenu() {
      const htmlElem = document.querySelector('html')
      this.mainMenuOpen = !this.mainMenuOpen
      // Disable scroll when open
      if (this.mainMenuOpen) {
        htmlElem.style.cssText = 'height: 100vh; overflow-y: hidden'
        this.$track.navigation.mobileMenuOpened()
      } else {
        htmlElem.style.cssText = ''
        this.$track.navigation.mobileMenuClosed()
      }
      // Reset active menus
      this.activeLevel2Menu = null
      this.activeLevel3Menu = null
    },

    handleLevel1Event(target) {
      this.$track.navigation.linkClicked({
        tierOne: target,
        location: 'header',
      })
    },

    toggleLevel2Menu(index, event, target) {
      /**
       * If it is a touch event then open the drop down menu
       */
      if(!['mouse'].includes(event.pointerType) && event.type === 'click') {
        event.preventDefault()
      }

      // Mobile
      if (!this.isMdBreakpointOrAbove) {
        event.preventDefault()
        this.activeLevel2Menu = index === this.activeLevel2Menu ? null : index
        this.activeLevel3Menu = null

        if (this.activeLevel2Menu != null && target) {
          this.handleLevel1Event(target)
        }
        // Desktop
      } else {
        this.handleLevel1Event(target)
      }
    },

    handleLevel2Event(target, parent) {
      const targetText = target.buttonText ? target.buttonText : target.linkText
      this.$track.navigation.linkClicked({
        tierTwo: targetText,
        tierOne: parent,
        location: 'header',
      })
    },

    toggleLevel3Menu(index) {
      this.activeLevel3Menu = index === this.activeLevel3Menu ? null : index
    },

    handleLevel3Event(target, parent, grandParent) {
      this.$track.navigation.linkClicked({
        tierThree: target,
        tierTwo: parent,
        tierOne: grandParent,
        location: 'header',
      })
    },

    handleLevel1HoverEnter(event) {
      const activeNavHeight = event.currentTarget.querySelector(
        '.MainNav__level-1-wrapper:hover .MainNav__level-2-links'
      )?.clientHeight
      if (activeNavHeight) {
        this.isBackdropActive = true
        this.backgroundHeight = `${activeNavHeight}px`
      }
    },

    handleLevel1HoverExit() {
      this.isBackdropActive = false
      this.backgroundHeight = '0px'
    },

    showCurrencyMenu() {
      this.sidebarStore.toggleCountrySelector()

      this.$track.navigation.showCurrencyMenu({
        location: 'header',
        currentCurrency: this.$i18n.localeProperties.currency,
      })
    },

    async handleCartClick() {
      this.shopifyStore.toggleCart(true)
      await this.$track.navigation.cartClicked()
    },

    setLevel3HeightForAccordion() {
      const level3Links = document.querySelectorAll('.MainNav__level-3-links')
      level3Links.forEach((link) => {
        const height = link.clientHeight
        link.style.setProperty('--link-height', `${height}px`)
      })
    },
  },

  mounted() {
    this.setLevel3HeightForAccordion()
    this.isLoaded = true
    requestIdleCallback(() => {
      document.addEventListener('scroll', this.throttledScrollHandler)
    })
  },

  beforeUnmount() {
    document.removeEventListener('scroll', this.throttledScrollHandler)
  },
})
</script>
<style lang="postcss">
.MainNav {
  --left-indent: 183px;

  @apply sticky top-0 isolate z-[12] w-full shadow-none
  -translate-y-0 max-h-[var(--nav-height)];
  transition: transform 500ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 250ms linear;

  /* Dropdown Content BG */
  &::after {
    @apply content-[''] bg-white
    absolute top-full w-full h-[100dvh]
    scale-x-0 origin-right
    transition-transform duration-500 delay-150;

    @screen lg {
      @apply h-[var(--background-height)] scale-x-100
      transition-[height] duration-500 delay-0;
    }
  }

  /* Backdrop Scrim */
  &::before {
    @apply content-[''] fixed inset-0 top-full h-screen
    bg-off-black bg-opacity-0 backdrop-blur-[0px]
    pointer-events-none
    transition-all duration-300 delay-300 lg:delay-0;
  }

  /**
    Minor State Changes
  */

  /* Before JS has loaded, disable hover functionality */
  @screen lg {
    &.MainNav--no-javascript .MainNav__level-1-wrapper {
      @apply pointer-events-none;
    }
  }

  /* Backdrop Scrim Active */
  &--is-backdrop-active::before {
    @apply bg-opacity-40 backdrop-blur-[4px];
  }

  &--is-scrolling-down {
    @apply -translate-y-full;
  }

  &--is-scrolled:not(.MainNav--is-scrolling-down) {
    @apply shadow;
  }
}

.MainNav__wrapper {
  @apply relative flex justify-between px-4
  bg-white bg-opacity-100
  transition-colors duration-300 delay-300;

  @screen lg {
    padding-left: var(--outer-gutter);
    padding-right: var(--outer-gutter);
  }
}

.MainNav__back-button {
  @apply relative ml-4 mb-4 flex items-center gap-x-2
  text-grey-600 font-semibold text-left
  opacity-0;

  @screen lg {
    @apply hidden;
  }
}

.MainNav__main {
  @apply flex w-full items-center gap-x-12;
}

.MainNav__logo {
  @apply w-24 z-[3] shrink-0;

  @screen lg {
    width: 6.5rem;
  }
}

.MainNav__logo,
.MainNav__utility-button {
  @apply py-[10px];
}

.MainNav__level-1-links {
  @apply absolute top-full left-0 w-full z-[2]
    pt-6
    h-[calc(100dvh_-_(var(--nav-height)_+_var(--promo-bar-height)))]
    transition-[clip-path] duration-500 delay-150;
  clip-path: inset(0% 0% 0% 100%);

  @screen lg {
    @apply relative flex h-full top-auto
    pt-0 overflow-visible bg-transparent;
    clip-path: none;
  }

  .MainNav__utility-item {
    @apply px-4 opacity-0 w-fit
    transition-all translate-x-5 duration-500 delay-0
    lg:hidden;
  }
}

.MainNav__level-1-wrapper {
  @apply self-center flex mb-3;

  @screen lg {
    @apply h-full mb-0;

    &:first-child {
      .MainNav__level-1-link {
        @apply pl-0;
      }
    }
  }
}

.MainNav__level-1-link {
  @apply self-center px-4
  font-semibold text-off-black text-base opacity-0
  transition-all translate-x-5 duration-500 delay-0;

  @screen lg {
    @apply px-5 xxl:px-6 opacity-100 translate-x-0 transition-none;
    font-size: clamp(0.8rem, 1.25vw, 0.9rem);
  }

  @screen xl {
    @apply text-base;
  }

  &--has-theme {
    .MainNav__level-1-link-text {
      --promo-gradient-percent: 65%;
      @apply promo-t-gradient-dark;

      &::after {
        background: linear-gradient(90deg,
          var(--promo-key, theme('colors.purple')) 0%,
          var(--promo-key, theme('colors.blue')) 65%,
          var(--promo-highlight, theme('colors.blue-light')) 100%)
      }
    }
  }
}

.MainNav__level-1-link-text {
  @apply relative py-2 block;

  @screen lg {
    @apply py-9;

    &:after {
      @apply content-[''] absolute bottom-2 left-0 w-full h-1
      bg-black
      scale-x-0 origin-right transition-transform duration-500;
    }
  }
}

.MainNav__level-1-button {
  @apply text-off-black text-base font-semibold px-4;
}

/* Desktop Hover State */
@screen lg {
  .MainNav__level-1-wrapper:hover {
    .MainNav__level-1-link-text {
      &:after {
        @apply scale-x-100 origin-left;
      }
    }

    .MainNav__level-2-wrapper {
      @apply delay-[350ms];
      clip-path: inset(0% 0% -1% 0%);
    }

    .MainNav__level-2-links {
      @apply pointer-events-auto;

      .MainNav__level-3-link {
        @apply opacity-100 -translate-y-0
        lg:delay-[calc(250ms_+_(var(--level-2-index)_*_100ms)_+_(var(--level-3-index)_*_25ms))];
      }

      .MainNav__level-2-link {
        @apply opacity-100 -translate-y-0
        lg:delay-[calc(250ms_+_(var(--level-2-index)_*_100ms))];
      }
    }
  }
}

.MainNav__level-2-links {
  @apply absolute top-0 left-0 z-[2] w-full h-full pt-5
  pointer-events-none overflow-y-scroll;

  @screen lg {
    @apply fixed top-full h-auto py-14
    overflow-visible pl-[var(--left-indent)];
  }

  @screen lgMax {
    @apply bg-white opacity-0
    transition-opacity duration-500 delay-300;

    .MainNav__back-button,
    .MainNav__level-2-heading,
    .MainNav__level-2-link {
      @apply translate-x-4 transition-all duration-300;
    }
  }
}

.MainNav__level-2-heading {
  @apply text-grey-600 font-semibold text-md mb-4 w-full opacity-0;

  @screen lg {
    @apply hidden;
  }
}

.MainNav__level-2-wrapper {
  @apply flex flex-col relative px-4;

  @screen lg {
    @apply flex-row gap-x-[clamp(3rem,20vw,5rem)] px-0 pr-10;
  }
}

.MainNav__level-2-links--is-link-grid {
  .MainNav__level-2-wrapper {
    @apply grid gap-y-4 pb-12 w-full;

    @screen lg {
      @apply gap-x-5 gap-y-10 pb-0;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }

    @screen xl {
      @apply pr-[var(--left-indent)];
    }
  }

  .MainNav__level-2-item {
    @apply w-[min-content] min-w-[15%];
  }

  .MainNav__level-2-link {
    @apply mb-0;
  }

  .MainNavLink__text {
    @apply whitespace-nowrap;
  }
}

.MainNav__level-3-link,
.MainNav__level-2-link {
  @apply opacity-0 transition-all duration-500;

  @screen lg {
    @apply -translate-y-2;
  }
}

.MainNav__level-2-links--is-image-grid {
  @screen lg {
    &:hover:has(.MainNav__level-2-item:hover)
      .MainNav__level-2-item:not(.MainNav__level-2-item:hover) {
      @apply opacity-[.64];
    }
  }

  .MainNav__level-2-wrapper {
    @apply flex-row flex-wrap gap-y-4 w-full pb-5;

    @screen lg {
      @apply pl-2 pr-8 flex-nowrap gap-x-8 overflow-x-scroll
      transition-[clip-path];
      clip-path: inset(0% 0% 18px 0%);
    }

    &::-webkit-scrollbar {
      width: 18px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-grey;
      border-radius: 18px;
      border: 6px solid transparent;
      background-clip: content-box;
    }

    &::-webkit-scrollbar-thumb:hover {
      @apply bg-off-black bg-opacity-30;
    }
  }

  .MainNav__level-2-item {
    @apply w-full;

    @screen lg {
      @apply w-auto min-w-[12rem] flex-shrink-0;
    }
  }

  .MainNav__level-2-item {
    @apply transition-opacity duration-300;
  }
}

.MainNav__level-2-links--is-image-grid {
  .MainNav__level-2-wrapper {
    @apply gap-x-10;
  }

  @screen lg {
    &.MainNav__level-2-links--is-product-image-grid .MainNav__level-2-wrapper {
      @apply gap-x-16;
    }
  }

  @screen lgMax {
    .MainNav__level-2-wrapper {
      @apply grid grid-cols-2;

      .MainNav__level-2-item,
      .MainNav__level-2-heading {
        @apply col-span-full;

        &:has(.MainNav__level-2-link .MainNavImageCard) {
          @apply col-span-1;
        }
      }
    }
  }
}

.MainNav__level-2-links--is-product-image-grid .MainNav__level-2-wrapper {
  @screen lgMax {
    @apply gap-x-8 overflow-hidden;
  }
}

.MainNav__level-2-link {
  @apply text-off-black font-semibold text-base mb-4;
}

.MainNav__level-3-heading {
  @apply w-full text-left;

  @screen lg {
    @apply pointer-events-none;
  }
}

.MainNav__level-3-links {
  --link-height: 0px;
  @screen lgMax {
    @apply overflow-hidden transition-[max-height] duration-500;

    /* Flatten on load */
    &--is-loaded {
      @apply max-h-0;
    }
  }
}

.MainNav__level-3-link {
  @apply text-off-black pb-3;

  @screen lgMax {
    @apply translate-x-2 duration-500 delay-0;
  }
}

.MainNav__level-3-link-inner {
  @screen lg {
    @apply relative
    font-semibold text-grey-600 hover:text-off-black
    transition-colors duration-300;

    &::after {
      @apply content-[''] w-full h-[1px] absolute -bottom-1 left-0
      bg-black
      scale-x-0 origin-right transition-transform duration-300;
    }

    &:hover::after {
      @apply scale-x-100 origin-left;
    }
  }
}

.MainNav__utilities {
  @apply w-full flex z-[2] justify-end;

  @screen lg {
    @apply w-auto gap-x-2;
  }

  .MainNav__utility-item--country {
    @apply lgMax:hidden;
  }
}

.MainNav__utility-item {
  @apply flex self-center;

  &:last-child {
    .MainNav__utility-button {
      @apply pr-0;
    }
  }
}

.MainNav__utility-item--country {
  @apply justify-center;
}

.MainNav__utility-item--menu {
  @screen lg {
    @apply hidden;
  }
}

.MainNav__utility-button {
  @apply h-full px-4;
}

.MainNav__utility-icon,
.MainNav__utility-icon-menu {
  @apply flex flex-wrap cursor-pointer w-5 relative;

  @screen md {
    @apply w-7;
  }
}

.MainNav__utility-icon-menu {
  @apply aspect-square relative flex items-center justify-center flex-col;

  &::before,
  &::after {
    @apply content-[''] absolute w-[calc(100%_+_1px)] h-0.5
    bg-off-black rounded-full
    transition-transform duration-500;
  }

  &::before {
    @apply translate-y-1;
  }

  &::after {
    @apply -translate-y-1;
  }
}

.MainNav__cart-icon-basket-count {
  @apply absolute flex items-center justify-center
    rounded-full
    bg-gradient
    text-[11px] md:text-[13px] text-white text-center font-bold;

  height: calc(0.9rem + 4px);
  width: calc(0.9rem + 4px);
  top: -6px;
  right: -9px;
  border: 2px solid #fff;

  @screen md {
    height: calc(1.15rem + 4px);
    width: calc(1.15rem + 4px);
  }
}

@keyframes basketCountLoader {
  from {
    opacity: 0%;
  }
  to {
    opacity: 25%;
  }
}

.MainNav__cart-icon-basket-count-loader {
  @apply block absolute w-full h-full top-0 left-0 bg-gradient rounded-full overflow-hidden;

  &::before {
    @apply content-[''] absolute w-full h-full top-0 left-0 bg-white;
    animation: basketCountLoader 0.6s infinite alternate;
  }
}

.MainNav__country-button-text {
  @apply flex items-center gap-x-1;
}

.MainNav__country-button-chevron {
  @apply flex items-center w-2 h-2;
}

/**

  States

*/

/* Mobile Open */
.MainNav--main-menu-open {
  &::after {
    @apply scale-x-100 delay-0;
  }

  &::before {
    @apply bg-opacity-40 backdrop-blur-[4px] delay-0;
  }

  .MainNav__wrapper {
    @apply delay-0;
  }

  .MainNav__level-1-links {
    @apply left-0 delay-0;
    clip-path: inset(0% 0% 0% 0%);
  }

  .MainNav__level-1-link,
  .MainNav__utility-item {
    @apply opacity-100 translate-x-0 delay-[calc(300ms_+_50ms_*_var(--level-1-index))];
  }

  .MainNav__level-2-links--open {
    @screen lgMax {
      @apply pointer-events-auto opacity-100 duration-500 delay-0;

      .MainNav__back-button,
      .MainNav__level-2-heading,
      .MainNav__level-2-link {
        @apply opacity-100 translate-x-0;
      }

      .MainNav__level-2-button {
        @apply delay-100;
      }

      .MainNav__level-2-heading {
        @apply delay-[125ms];
      }

      .MainNav__level-2-link {
        @apply delay-[calc(150ms_+_(25ms_*_var(--level-2-index)))];
      }

      .MainNavButtonCard__level-3-heading {
        &::before,
        &::after {
          @apply opacity-100 delay-[calc(150ms_+_(25ms_*_var(--level-2-index)))];
        }
      }
    }
  }

  .MainNav__level-3-links--open {
    @screen lgMax {
      @apply max-h-[var(--link-height)];
    }

    .MainNav__level-3-link {
      @apply opacity-100 translate-x-0 duration-500 delay-[calc(75ms_*_var(--level-3-index))];
    }
  }

  .MainNav__utility-icon-menu {
    &::before {
      @apply translate-y-0 rotate-45;
    }

    &::after {
      @apply -translate-y-0 -rotate-45;
    }
  }
}

/**

  Large Modifiers

*/

/* Dark Chrome */
.is-dark-chrome-theme {
  .MainNav:not(.MainNav--main-menu-open) .MainNav__wrapper {
    @apply bg-off-black;
  }

  @screen lg {
    .MainNav:hover .MainNav__wrapper {
      @apply bg-white delay-0;
    }
  }
}

/* Transparent */
.MainNav--is-transparent {
  @apply -mb-[var(--nav-height)];

  &:not(.MainNav--is-scrolled) {
    .MainNav__wrapper {
      @apply bg-opacity-0;
    }

    &.MainNav--main-menu-open {
      .MainNav__wrapper {
        @apply bg-opacity-100;
      }
    }

    @screen lg {
      &:hover {
        .MainNav__wrapper {
          @apply bg-opacity-100 delay-0;
        }
      }
    }
  }
}

/* Inverted (White) */
.MainNav--is-inverted.MainNav--is-transparent:not(
    .is-error .MainNav,
    .MainNav--is-scrolled,
    .MainNav--main-menu-open,
    .MainNav--show-black-links
  ), .is-dark-chrome-theme .MainNav:not(
    .MainNav--is-scrolled,
    .MainNav--main-menu-open,
    .MainNav--show-black-links
  ) {
  .MainNav__utility-icon-menu {
    &::before,
    &::after {
      @apply bg-white transition-colors delay-200;
    }
  }

  .MainNav__level-1-link {
    @apply lg:text-white lg:transition-colors lg:delay-200;
  }

  .VisionButton--bg-black.VisionButton--outline {
    @apply border-white transition-colors delay-200;
  }

  .VisionIcon--colour-black .VisionIcon__svg,
  .BaseLogo {
    @apply fill-white transition-colors delay-200;
  }

  .MainNav__country-button-text {
    @apply text-white transition-colors delay-200;
  }

  .MainNav__country-button:hover {
    .MainNav__country-button-text {
      @apply text-white;
    }
  }

  @screen lg {
    &:hover {
      .MainNav__utility-icon-menu {
        &::before,
        &::after {
          @apply bg-off-black delay-0;
        }
      }

      .MainNav__level-1-link {
        @apply text-off-black delay-0;
      }

      .VisionButton--bg-black.VisionButton--outline {
        @apply border-off-black delay-0;
      }

      .VisionIcon--colour-black .VisionIcon__svg,
      .BaseLogo {
        @apply fill-off-black delay-0;
      }

      .MainNav__country-button-text {
        @apply text-off-black delay-0;
      }
    }
  }
}
</style>
